import { copyLineDown } from "@codemirror/commands";
import { SearchCursor } from "@codemirror/search";
import {
  EditorSelection,
  type EditorState,
  type SelectionRange,
} from "@codemirror/state";
import type { EditorView } from "@codemirror/view";

// Helper to get word selection if current selection is empty
function getWordSelection(
  state: EditorState,
  pos: number,
): SelectionRange | null {
  const line = state.doc.lineAt(pos);
  const text = line.text;
  const lineStart = line.from;
  const relativePos = pos - lineStart;

  // Simple word boundary: non-alphanumeric characters
  const isWordChar = (char: string) => /\w/.test(char);

  let start = relativePos;
  while (start > 0 && isWordChar(text[start - 1])) {
    start--;
  }

  let end = relativePos;
  while (end < text.length && isWordChar(text[end])) {
    end++;
  }

  if (start === end) {
    return null; // No word found
  }

  return EditorSelection.range(lineStart + start, lineStart + end);
}

export const selectNextOccurrenceCommand = (view: EditorView) => {
  const { state, dispatch } = view;
  let currentRanges = state.selection.ranges.slice();
  let mainRange = state.selection.main;

  if (mainRange.empty) {
    const wordSelection = getWordSelection(state, mainRange.head);
    if (!wordSelection) {
      return false;
    }
    mainRange = wordSelection;
    currentRanges = [wordSelection];
  }

  const query = state.sliceDoc(mainRange.from, mainRange.to);
  const cursor = new SearchCursor(state.doc, query, mainRange.to);
  cursor.next();

  if (!cursor.done) {
    const nextMatch = cursor.value;
    const newRanges = currentRanges.slice();
    const newSelection = EditorSelection.range(nextMatch.from, nextMatch.to);

    const isAlreadySelected = newRanges.some(
      (r) => r.from === newSelection.from && r.to === newSelection.to,
    );

    if (!isAlreadySelected) {
      newRanges.push(newSelection);
    }

    dispatch(
      view.state.update({
        selection: EditorSelection.create(newRanges, newRanges.length - 1),
        scrollIntoView: true,
        userEvent: "select",
      }),
    );
    return true;
  }
  return false;
};

export const selectPreviousOccurrenceCommand = (view: EditorView) => {
  const { state, dispatch } = view;
  const currentRanges = state.selection.ranges.slice();

  // If we have multiple selections, the user likely wants to "undo" the last addition
  // (which corresponds to the "Select Previous" logic in many editors when strictly adding).
  if (currentRanges.length > 1) {
    const newRanges = currentRanges.slice(0, -1);
    dispatch(
      view.state.update({
        selection: EditorSelection.create(newRanges, newRanges.length - 1),
        scrollIntoView: true,
        userEvent: "select",
      }),
    );
    return true;
  }

  // If single selection, try to find and add the previous occurrence
  let mainRange = state.selection.main;

  if (mainRange.empty) {
    const wordSelection = getWordSelection(state, mainRange.head);
    if (!wordSelection) {
      return false;
    }
    mainRange = wordSelection;
    currentRanges = [wordSelection];
  }

  const query = state.sliceDoc(mainRange.from, mainRange.to);

  const matchesBefore: { from: number; to: number }[] = [];
  const cursor = new SearchCursor(state.doc, query, 0, mainRange.from);
  while (!cursor.next().done) {
    matchesBefore.push(cursor.value);
  }

  const previousMatch = matchesBefore.pop();

  if (previousMatch) {
    const newRanges = currentRanges.slice();
    const newSelection = EditorSelection.range(
      previousMatch.from,
      previousMatch.to,
    );

    const isAlreadySelected = newRanges.some(
      (r) => r.from === newSelection.from && r.to === newSelection.to,
    );

    if (!isAlreadySelected) {
      newRanges.push(newSelection);
    }

    dispatch(
      view.state.update({
        selection: EditorSelection.create(newRanges, newRanges.length - 1),
        scrollIntoView: true,
        userEvent: "select",
      }),
    );
    return true;
  }
  return false;
};

export const skipCurrentAndSelectNextCommand = (view: EditorView) => {
  const { state, dispatch } = view;
  const currentRanges = state.selection.ranges.slice();
  const mainRange = state.selection.main;

  if (mainRange.empty) {
    return false;
  }

  const query = state.sliceDoc(mainRange.from, mainRange.to);

  const rangesWithoutMain = currentRanges.filter(
    (r) => r.from !== mainRange.from || r.to !== mainRange.to,
  );

  const cursor = new SearchCursor(state.doc, query, mainRange.to);
  cursor.next();

  if (!cursor.done) {
    const nextMatch = cursor.value;
    const newRanges = rangesWithoutMain.slice();
    newRanges.push(EditorSelection.range(nextMatch.from, nextMatch.to));
    dispatch(
      view.state.update({
        selection: EditorSelection.create(newRanges, newRanges.length - 1),
        scrollIntoView: true,
        userEvent: "select",
      }),
    );
    return true;
  } else {
    if (rangesWithoutMain.length < currentRanges.length) {
      dispatch(
        view.state.update({
          selection: EditorSelection.create(
            rangesWithoutMain,
            rangesWithoutMain.length > 0 ? rangesWithoutMain.length - 1 : 0,
          ),
          scrollIntoView: true,
          userEvent: "select",
        }),
      );
      return true;
    }
  }
  return false;
};

export const skipCurrentAndSelectPreviousCommand = (view: EditorView) => {
  const { state, dispatch } = view;
  const currentRanges = state.selection.ranges.slice();
  const mainRange = state.selection.main;

  if (mainRange.empty) {
    return false;
  }

  const query = state.sliceDoc(mainRange.from, mainRange.to);

  const rangesWithoutMain = currentRanges.filter(
    (r) => r.from !== mainRange.from || r.to !== mainRange.to,
  );

  const matchesBefore: { from: number; to: number }[] = [];
  const cursor = new SearchCursor(state.doc, query, 0, mainRange.from);
  while (!cursor.next().done) {
    matchesBefore.push(cursor.value);
  }

  const previousMatch = matchesBefore.pop();

  if (previousMatch) {
    const newRanges = rangesWithoutMain.slice();
    newRanges.push(EditorSelection.range(previousMatch.from, previousMatch.to));
    dispatch(
      view.state.update({
        selection: EditorSelection.create(newRanges, newRanges.length - 1),
        scrollIntoView: true,
        userEvent: "select",
      }),
    );
    return true;
  } else {
    if (rangesWithoutMain.length < currentRanges.length) {
      dispatch(
        view.state.update({
          selection: EditorSelection.create(
            rangesWithoutMain,
            rangesWithoutMain.length > 0 ? rangesWithoutMain.length - 1 : 0,
          ),
          scrollIntoView: true,
          userEvent: "select",
        }),
      );
      return true;
    }
  }
  return false;
};

export const duplicateSelectionOrLine = (view: EditorView) => {
  const { state } = view;
  if (state.selection.main.empty) {
    return copyLineDown(view);
  }

  const changes = state.selection.ranges
    .map((range) => {
      if (range.empty) {
        return null;
      }
      return {
        from: range.to,
        insert: state.sliceDoc(range.from, range.to),
      };
    })
    .filter((x): x is { from: number; insert: string } => x !== null);

  if (changes.length === 0) {
    return copyLineDown(view);
  }

  view.dispatch({
    changes,
    scrollIntoView: true,
  });
  return true;
};

export const toUpperCase = (view: EditorView) => {
  return changeCase(view, (s) => s.toUpperCase());
};

export const toLowerCase = (view: EditorView) => {
  return changeCase(view, (s) => s.toLowerCase());
};

function changeCase(view: EditorView, transform: (s: string) => string) {
  const { state } = view;
  const changes = state.changeByRange((range) => {
    if (range.empty) {
      return { range };
    }
    return {
      changes: {
        from: range.from,
        to: range.to,
        insert: transform(state.sliceDoc(range.from, range.to)),
      },
      range,
    };
  });
  view.dispatch(changes);
  return true;
}

export const addCursorLineAbove = (view: EditorView) => {
  return addCursorLine(view, -1);
};

export const addCursorLineBelow = (view: EditorView) => {
  return addCursorLine(view, 1);
};

export const insertLineAbove = (view: EditorView) => {
  const { state, dispatch } = view;
  const changes = state.changeByRange((range) => {
    const line = state.doc.lineAt(range.head);
    return {
      changes: { from: line.from, insert: "\n" },
      range: EditorSelection.cursor(line.from),
    };
  });
  dispatch(changes);
  return true;
};

function addCursorLine(view: EditorView, dir: 1 | -1) {
  const { state } = view;
  const newRanges: SelectionRange[] = [];

  for (const range of state.selection.ranges) {
    newRanges.push(range);

    const line = state.doc.lineAt(range.head);
    const targetLineNumber = line.number + dir;

    if (targetLineNumber < 1 || targetLineNumber > state.doc.lines) {
      continue;
    }

    const targetLine = state.doc.line(targetLineNumber);
    const col = range.head - line.from;
    const targetPos = Math.min(targetLine.from + col, targetLine.to);

    newRanges.push(EditorSelection.cursor(targetPos));
  }

  if (newRanges.length > state.selection.ranges.length) {
    view.dispatch({
      selection: EditorSelection.create(newRanges),
      scrollIntoView: true,
    });
    return true;
  }

  return false;
}
